/**
 * Figure Sequences generator (EXAM.md §3, PLAN.md §2c).
 *
 * A 4×4 matrix shows 4 stem frames followed by 2 blanks; each blank offers 3
 * options and is scored INDEPENDENTLY (one item → two responses). Nothing
 * animates: the generator steps each figure's rule deterministically over 6
 * frames and renders still frames.
 *
 * The FULL rule pool is available at every difficulty (the documented
 * departure from EXAM.md §3's ladder): a low item is a quieter rehearsal for
 * a hard one, not a different kind of puzzle. Difficulty is MEASURED
 * tracking load — figure count plus per-rule complexity — together with
 * distractor subtlety:
 *   base 1 · rotation +1 · 2-colour +1 · 3-colour +2 ·
 *   border traversal or multi-segment direction cycle +1 · x+1 +2
 * Bands: low = 1–2 figures, load ≤ 4 · medium = 3 figures, load 5–9 ·
 * high = 4 figures, load ≥ 10. The six official exercises measure
 * 1, 1, 7, 6, 13 and 13 under these weights — landing exactly in their
 * published bands (locked in the fixture tests).
 *
 * Invariants are enforced at generation time and re-checked by the tests:
 * figures never leave the grid, never overlap in any frame, never disappear,
 * and a diagonal mover never switches movement type.
 *
 * This module must stay DOM-free and dependency-free (PLAN-Z convention 4).
 */
import { mulberry32, pick, randomInt, shuffle } from './prng.ts'
import type { Difficulty, Question, Step } from './types.ts'

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

export const FIGURE_COLOURS = ['blue', 'teal', 'orange', 'yellow', 'pink', 'white', 'black'] as const
export type FigureColour = (typeof FIGURE_COLOURS)[number]

export type FigureShape =
  | 'star'
  | 'triangle'
  | 'arrow'
  | 'bracket'
  | 'arc'
  | 'square'
  | 'diamond'
  | 'hexagon'

/** Shapes that visibly change under a 90° rotation and may carry rotation
 * rules. The star (four-point star in a diamond), square, diamond and hexagon
 * are rotationally symmetric (square/diamond to 90°, hexagon to 60° — a 90°
 * step maps the hexagon onto itself) and never rotate (EXAM.md §3 style). */
export const ROTATABLE_SHAPES: readonly FigureShape[] = ['triangle', 'arrow', 'bracket', 'arc']
export function isRotatable(shape: FigureShape): boolean {
  return (ROTATABLE_SHAPES as readonly string[]).includes(shape)
}

/** Human-readable names used in explanations ("the teal L-shape"). */
export const SHAPE_NAMES: Record<FigureShape, string> = {
  star: 'star',
  triangle: 'triangle',
  arrow: 'arrow',
  bracket: 'L-shape',
  arc: 'arc',
  square: 'square',
  diamond: 'diamond',
  hexagon: 'hexagon',
}

export type Cardinal = 'up' | 'down' | 'left' | 'right'
export type Diagonal = 'up-right' | 'up-left' | 'down-right' | 'down-left'
export type AnyDir = Cardinal | Diagonal

const DELTA: Record<AnyDir, readonly [number, number]> = {
  up: [-1, 0],
  down: [1, 0],
  left: [0, -1],
  right: [0, 1],
  'up-right': [-1, 1],
  'up-left': [-1, -1],
  'down-right': [1, 1],
  'down-left': [1, -1],
}

function dirFromDelta(dr: number, dc: number): AnyDir {
  for (const [dir, [ddr, ddc]] of Object.entries(DELTA)) {
    if (ddr === dr && ddc === dc) return dir as AnyDir
  }
  throw new Error(`not a unit delta: (${dr}, ${dc})`)
}

const isDiagonal = (dir: AnyDir): boolean => dir.length > 5 // 'up-right' etc.

export type MoveRule =
  | { kind: 'linear'; dir: AnyDir }
  | { kind: 'border'; cw: boolean; step: 1 | 2 | 'x+1' }
  | { kind: 'cycle'; dirs: Cardinal[] }

export interface FigureRule {
  move: MoveRule
  /** 90° steps per frame, CW (1) or CCW (−1); 'x+1' = step count grows per frame. */
  rotate: { dir: 1 | -1; step: 1 | 'x+1' } | null
  /** 1 static, 2 alternation, 3 rotating cycle (EXAM.md §3 palette). */
  colours: FigureColour[]
  colourStep: 1 | 'x+1'
}

export interface FigureSpec {
  shape: FigureShape
  start: { row: number; col: number }
  orientation: number
  rule: FigureRule
}

export interface FigureState {
  row: number
  col: number
  shape: FigureShape
  colour: FigureColour
  orientation: number
}

export type Frame = FigureState[]

/** Outer border cells of the 4×4 grid, clockwise from the top-left corner. */
export const BORDER_CELLS: readonly (readonly [number, number])[] = [
  [0, 0], [0, 1], [0, 2], [0, 3],
  [1, 3], [2, 3], [3, 3],
  [3, 2], [3, 1], [3, 0],
  [2, 0], [1, 0],
]

// ---------------------------------------------------------------------------
// Deterministic stepper
// ---------------------------------------------------------------------------

/** Step count for a transition: constant, or x+1 (1, then 2, then 3, …). */
const stepsFor = (step: 1 | 'x+1', transition: number): number =>
  step === 'x+1' ? transition + 1 : 1

/** The figure's state across `frames` frames; frame 1 is the start state. */
export function trajectory(spec: FigureSpec, frames = 6): FigureState[] {
  const states: FigureState[] = []
  let row = spec.start.row
  let col = spec.start.col
  let orientation = spec.orientation
  let colourIdx = 0
  let dir = spec.rule.move.kind === 'linear' ? spec.rule.move.dir : null
  const push = () =>
    states.push({
      row,
      col,
      shape: spec.shape,
      colour: spec.rule.colours[((colourIdx % spec.rule.colours.length) + spec.rule.colours.length) % spec.rule.colours.length],
      orientation: ((orientation % 4) + 4) % 4,
    })
  push()

  for (let t = 0; t < frames - 1; t++) {
    const move = spec.rule.move
    if (move.kind === 'linear') {
      let [dr, dc] = DELTA[dir!]
      const nr = row + dr
      const nc = col + dc
      if (nr < 0 || nr > 3 || nc < 0 || nc > 3) {
        // Bounce: a straight mover reverses along its path; a diagonal bouncer
        // reverses fully and retraces its path exactly (EXAM.md §3).
        dr = -dr
        dc = -dc
      }
      dir = dirFromDelta(dr, dc)
      row += dr
      col += dc
    } else if (move.kind === 'border') {
      const idx = BORDER_CELLS.findIndex(([r, c]) => r === row && c === col)
      const k = move.step === 'x+1' ? t + 1 : move.step
      const next = (((idx + (move.cw ? k : -k)) % 12) + 12) % 12
      ;[row, col] = BORDER_CELLS[next]
    } else {
      const d = move.dirs[t % move.dirs.length]
      row += DELTA[d][0]
      col += DELTA[d][1]
    }
    if (spec.rule.rotate) {
      orientation += spec.rule.rotate.dir * stepsFor(spec.rule.rotate.step, t)
    }
    colourIdx += stepsFor(spec.rule.colourStep, t)
    push()
  }
  return states
}

// ---------------------------------------------------------------------------
// Tracking load and invariants
// ---------------------------------------------------------------------------

/** Load contributed by a single figure's rule (PLAN.md §2c weights). */
export function figureLoad(rule: FigureRule): number {
  let load = 1
  if (rule.rotate) load += 1
  if (rule.colours.length === 2) load += 1
  if (rule.colours.length === 3) load += 2
  if (rule.move.kind === 'border' || (rule.move.kind === 'cycle' && rule.move.dirs.length >= 2)) {
    load += 1
  }
  if (rule.move.kind === 'border' && rule.move.step === 'x+1') load += 2
  if (rule.rotate?.step === 'x+1') load += 2
  if (rule.colourStep === 'x+1') load += 2
  return load
}

export function trackingLoad(specs: FigureSpec[]): number {
  return specs.reduce((sum, s) => sum + figureLoad(s.rule), 0)
}

/** Invariants (EXAM.md §3): never off-grid, never overlapping, never
 * disappearing (fixed count by construction), diagonal movers never switch
 * movement type. */
export function checkInvariants(specs: FigureSpec[]): boolean {
  const trajs = specs.map((s) => trajectory(s))
  for (const traj of trajs) {
    for (const st of traj) {
      if (st.row < 0 || st.row > 3 || st.col < 0 || st.col > 3) return false
    }
  }
  for (let i = 0; i < trajs.length; i++) {
    for (let j = i + 1; j < trajs.length; j++) {
      for (let f = 0; f < trajs[i].length; f++) {
        if (trajs[i][f].row === trajs[j][f].row && trajs[i][f].col === trajs[j][f].col) return false
      }
    }
  }
  for (const spec of specs) {
    if (spec.rule.move.kind === 'linear' && isDiagonal(spec.rule.move.dir)) {
      const traj = trajectory(spec)
      for (let f = 1; f < traj.length; f++) {
        const dr = Math.abs(traj[f].row - traj[f - 1].row)
        const dc = Math.abs(traj[f].col - traj[f - 1].col)
        if (dr !== 1 || dc !== 1) return false
      }
    }
  }
  return true
}

// ---------------------------------------------------------------------------
// Distractors: single-perturbation near-misses (EXAM.md §3 generator rules)
// ---------------------------------------------------------------------------

export function frameEquals(a: Frame, b: Frame): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Per-figure difficulty, used to pick the perturbation target: at low we
 * perturb the most obvious figure, at high the subtlest one (PLAN.md §2c). */
function subtletyOrder(specs: FigureSpec[], high: boolean): number[] {
  return specs
    .map((s, i) => ({ i, load: figureLoad(s.rule) }))
    .sort((x, y) => (high ? y.load - x.load : x.load - y.load))
    .map((x) => x.i)
}

function perturb(
  specs: FigureSpec[],
  base: Frame,
  blankFrameIndex: number,
  figIdx: number,
  mode: 'extra' | 'short' | 'rotate' | 'colour',
): Frame | null {
  const spec = specs[figIdx]
  const frame = base.map((s) => ({ ...s }))
  if (mode === 'extra') {
    const longer = trajectory(spec, blankFrameIndex + 2)
    const st = longer[blankFrameIndex + 1]
    if (!st) return null
    frame[figIdx] = st
  } else if (mode === 'short') {
    const shorter = trajectory(spec, blankFrameIndex) // one transition less
    frame[figIdx] = shorter[blankFrameIndex - 1]
  } else if (mode === 'rotate') {
    if (!isRotatable(spec.shape) || !spec.rule.rotate) return null // rotationally symmetric — invisible
    frame[figIdx] = { ...frame[figIdx], orientation: (frame[figIdx].orientation + 1) % 4 }
  } else {
    if (spec.rule.colours.length < 2) return null
    const shifted = frame[figIdx]
    const idx = spec.rule.colours.indexOf(shifted.colour)
    frame[figIdx] = { ...shifted, colour: spec.rule.colours[(idx + 1) % spec.rule.colours.length] }
  }
  return frame
}

function buildDistractors(
  specs: FigureSpec[],
  stem: Frame[],
  answerFrame: Frame,
  blankFrameIndex: number,
  high: boolean,
  draw: () => number,
): Frame[] | null {
  const distractors: Frame[] = []
  const taken = [answerFrame, ...stem]
  const order = shuffle(draw, ['extra', 'short', 'rotate', 'colour'] as const)
  const figureOrder = shuffle(draw, subtletyOrder(specs, high))
  for (const mode of order) {
    for (const figIdx of figureOrder) {
      if (distractors.length === 2) return distractors
      const candidate = perturb(specs, answerFrame, blankFrameIndex, figIdx, mode)
      if (!candidate) continue
      if (taken.some((t) => frameEquals(t, candidate))) continue
      // a distractor must stay on-grid and must not show two figures on one cell
      const onGrid = candidate.every((s) => s.row >= 0 && s.row <= 3 && s.col >= 0 && s.col <= 3)
      if (!onGrid) continue
      const cells = new Set(candidate.map((s) => `${s.row},${s.col}`))
      if (cells.size !== candidate.length) continue
      distractors.push(candidate)
      taken.push(candidate)
    }
  }
  return distractors.length === 2 ? distractors : null
}

// ---------------------------------------------------------------------------
// Official-style rule descriptions (EXAM.md §3 solution-key phrasing)
// ---------------------------------------------------------------------------

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth']

/** Describes a figure's rule the way the official solutions do — the movement
 * within the grid, then its rotation and colour behaviour. */
export function describeRule(spec: FigureSpec): string {
  const { rule, start } = spec
  const parts: string[] = []

  if (rule.move.kind === 'linear') {
    const dir = rule.move.dir
    if (dir === 'up' || dir === 'down') {
      parts.push(
        `moves vertically one field at a time in the ${ORDINALS[start.col]} column and bounces off the upper/lower boundary`,
      )
    } else if (dir === 'left' || dir === 'right') {
      parts.push(
        `moves horizontally one field at a time in the ${ORDINALS[start.row]} row and bounces off the left/right border`,
      )
    } else {
      parts.push(
        `moves diagonally ${dir} one field at a time; on hitting a boundary it bounces off and retraces its path exactly`,
      )
    }
  } else if (rule.move.kind === 'border') {
    const dirWord = rule.move.cw ? 'clockwise' : 'counterclockwise'
    const stepWord =
      rule.move.step === 1 ? 'one square' : rule.move.step === 2 ? 'two squares' : 'x + 1 squares'
    parts.push(`moves along the outer borders ${dirWord} by ${stepWord} at a time`)
  } else {
    parts.push(
      `moves one field per step following the direction cycle ${rule.move.dirs.join(', ')}, and so on`,
    )
  }

  if (rule.rotate) {
    const dirWord = rule.rotate.dir === 1 ? 'right' : 'left'
    parts.push(
      rule.rotate.step === 'x+1'
        ? `rotates 90° ${dirWord} x + 1 times per step`
        : `rotates 90° ${dirWord} from image to image`,
    )
  }

  if (rule.colours.length === 2) {
    parts.push(`alternates ${rule.colours[0]} ↔ ${rule.colours[1]}`)
  } else if (rule.colours.length === 3) {
    parts.push(`cycles colour ${rule.colours.join(' → ')} → …`)
  }

  return parts.join(', and ')
}

// ---------------------------------------------------------------------------
// Generation
// ---------------------------------------------------------------------------

export interface FiguresPrompt {
  /** Frames 1–4 of the sequence. */
  stem: Frame[]
  /** Three options under blank 1 (frame 5) and blank 2 (frame 6). */
  options: [Frame, Frame, Frame][]
  /** Measured tracking load (PLAN.md §2c) — reported per item. */
  trackingLoad: number
}

export interface FiguresAnswer {
  /** Indices (0–2) of the correct option per blank. */
  correct: [number, number]
}

const SHAPES: FigureShape[] = ['star', 'triangle', 'arrow', 'bracket', 'arc', 'square', 'diamond', 'hexagon']
const CARDINALS: Cardinal[] = ['up', 'down', 'left', 'right']
const ALL_DIRS: AnyDir[] = [...CARDINALS, 'up-right', 'up-left', 'down-right', 'down-left']

const BANDS: Record<Difficulty, { min: number; max: number; count: (draw: () => number) => number }> = {
  low: { min: 0, max: 4, count: (draw) => (draw() < 0.5 ? 1 : 2) },
  medium: { min: 5, max: 9, count: () => 3 },
  high: { min: 10, max: 24, count: () => 4 },
}

/** Feature probabilities scale with difficulty (richer rules at higher
 * levels) while the POOL stays constant at every level. */
function drawFigure(
  draw: () => number,
  difficulty: Difficulty,
  shape: FigureShape,
): FigureSpec {
  const rich = difficulty === 'high' ? 0.6 : difficulty === 'medium' ? 0.45 : 0.2
  const start = { row: randomInt(draw, 0, 3), col: randomInt(draw, 0, 3) }

  const kind = pick(draw, ['linear', 'border', 'cycle'] as const)
  let move: MoveRule
  if (kind === 'linear') {
    move = { kind: 'linear', dir: pick(draw, ALL_DIRS) }
  } else if (kind === 'border') {
    move = {
      kind: 'border',
      cw: draw() < 0.5,
      step: draw() < rich ? 'x+1' : (pick(draw, [1, 2]) as 1 | 2),
    }
    // border movers must start on the border
    const b = BORDER_CELLS[randomInt(draw, 0, 11)]
    start.row = b[0]
    start.col = b[1]
  } else {
    const dirs = shuffle(draw, [...CARDINALS]).slice(0, randomInt(draw, 2, 4))
    move = { kind: 'cycle', dirs }
  }

  const rotate =
    isRotatable(shape) && draw() < 0.3 + rich * 0.4
      ? { dir: (draw() < 0.5 ? 1 : -1) as 1 | -1, step: draw() < rich * 0.5 ? ('x+1' as const) : (1 as const) }
      : null

  const colourDraw = draw()
  const colours: FigureColour[] =
    colourDraw < 0.35 - rich * 0.15
      ? [pick(draw, FIGURE_COLOURS)]
      : colourDraw < 0.7
        ? shuffle(draw, [...FIGURE_COLOURS]).slice(0, 2)
        : shuffle(draw, [...FIGURE_COLOURS]).slice(0, 3)

  const rule: FigureRule = {
    move,
    rotate,
    colours,
    colourStep: colours.length > 1 && draw() < rich * 0.6 ? 'x+1' : 1,
  }
  return { shape, start, orientation: isRotatable(shape) ? randomInt(draw, 0, 3) : 0, rule }
}

/** Generate one figure-sequences item. Pure: same (difficulty, seed) → same question. */
export function generateFigures(
  difficulty: Difficulty,
  seed: number,
): Question<FiguresPrompt, FiguresAnswer> {
  const draw = mulberry32(seed)
  const band = BANDS[difficulty]
  for (let attempt = 0; attempt < 400; attempt++) {
    const count = band.count(draw)
    // no two figures in a question share a shape
    const shapes = shuffle(draw, [...SHAPES]).slice(0, count)
    const specs: FigureSpec[] = []
    for (const shape of shapes) specs.push(drawFigure(draw, difficulty, shape))

    const load = trackingLoad(specs)
    if (load < band.min || load > band.max) continue
    if (!checkInvariants(specs)) continue

    const trajectories = specs.map((s) => trajectory(s, 7))
    const frame = (f: number): Frame => trajectories.map((t) => t[f])
    const stem = [0, 1, 2, 3].map(frame)
    const blank1 = frame(4)
    const blank2 = frame(5)

    const d1 = buildDistractors(specs, stem, blank1, 4, difficulty === 'high', draw)
    if (!d1) continue
    const d2 = buildDistractors(specs, stem, blank2, 5, difficulty === 'high', draw)
    if (!d2) continue

    // correct-option slot varies per blank (2 distractors + the answer = 3)
    const correct1 = randomInt(draw, 0, 2)
    const correct2 = randomInt(draw, 0, 2)
    const withAnswerSlot = (distractors: Frame[], answerFrame: Frame, slot: number): [Frame, Frame, Frame] => {
      const copy = [...distractors]
      copy.splice(slot, 0, answerFrame)
      return copy as [Frame, Frame, Frame]
    }
    const options1 = withAnswerSlot(d1, blank1, correct1)
    const options2 = withAnswerSlot(d2, blank2, correct2)

    // official-style rule descriptions: movement within the grid, then
    // rotation and colour behaviour (EXAM.md §3 solution-key phrasing)
    const steps: Step[] = specs.map((spec, i) => ({
      text: `Symbol ${i + 1} — the ${spec.rule.colours[0]} ${SHAPE_NAMES[spec.shape]}: ${describeRule(spec)}.`,
    }))
    steps.push({
      text: `Blank 1 (frame 5) shows option ${correct1 + 1}; blank 2 (frame 6) shows option ${correct2 + 1}.`,
    })

    return {
      subtest: 'figures',
      difficulty,
      seed,
      prompt: { stem, options: [options1, options2], trackingLoad: load },
      answer: { correct: [correct1, correct2] },
      explanation: steps,
    }
  }
  throw new Error(`figures: failed to construct a valid item for seed ${seed} (${difficulty})`)
}
